import axios from "axios";
import { getConfigValue } from "../config/appConfig";

const STORAGE_KEY = "school_subscription";
const TRIAL_EXPIRED_KEY = "trial_expired";
const SUBSCRIPTION_CANCELLED_PREFIX = "subscription_cancelled_";

const TRIAL_DAYS = 30;

export function normalizeSchoolPayload(data) {
  if (data == null || typeof data !== "object") return null;

  if (
    data.planType != null ||
    data.plan_type != null ||
    data.isTrialExpired != null ||
    data.canAccessDashboard != null ||
    data.trialEndDate != null ||
    data.trial_end_date != null
  ) {
    return data;
  }

  if (data.id != null || data.schoolId != null) {
    return {
      planType: data.planType ?? data.plan_type ?? "trial",
      trialStartDate: data.trialStartDate ?? data.trial_start_date,
      trialEndDate: data.trialEndDate ?? data.trial_end_date,
      created_at: data.created_at ?? data.createdAt,
      schoolId: data.schoolId ?? data.id,
      schoolName: data.schoolName,
      isTrialExpired: data.isTrialExpired,
      canAccessDashboard: data.canAccessDashboard,
    };
  }

  return null;
}

export function extractSubscriptionFromResponse(apiResponse) {
  if (!apiResponse) return null;
  const data = apiResponse.data ?? apiResponse.subscription ?? apiResponse;
  if (data == null || typeof data !== "object") return null;
  return normalizeSchoolPayload(data);
}

export function evaluateSubscription(subscription) {
  if (!subscription) {
    return { canAccess: true, isExpired: false };
  }

  const planType = String(
    subscription.planType ?? subscription.plan_type ?? "trial"
  ).toLowerCase();

  if (planType === "paid") {
    return { canAccess: true, isExpired: false };
  }

  if (
    subscription.canAccessDashboard === false ||
    subscription.isTrialExpired === true ||
    subscription.trialExpired === true
  ) {
    return { canAccess: false, isExpired: true };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parseDate = (value) => {
    if (!value) return null;
    const str = String(value).slice(0, 10);
    const parsed = new Date(`${str}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  let trialEnd = parseDate(subscription.trialEndDate ?? subscription.trial_end_date);

  const trialStart = parseDate(
    subscription.trialStartDate ??
      subscription.trial_start_date ??
      subscription.createdAt ??
      subscription.created_at
  );

  if (!trialEnd && trialStart) {
    trialEnd = new Date(trialStart);
    trialEnd.setDate(trialEnd.getDate() + TRIAL_DAYS);
  }

  if (trialEnd) {
    const expired = trialEnd < today;
    return { canAccess: !expired, isExpired: expired };
  }

  if (planType === "trial" || planType === "free_trial") {
    return { canAccess: true, isExpired: false };
  }

  return { canAccess: true, isExpired: false };
}

export function persistSubscription(subscription, signupData = {}) {
  if (!subscription) return;

  const payload = {
    ...subscription,
    schoolId: signupData.schoolId ?? subscription.schoolId,
    schoolName: signupData.schoolName ?? subscription.schoolName,
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function getStoredSubscription() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setTrialExpiredFlag(isExpired) {
  if (isExpired) {
    localStorage.setItem(TRIAL_EXPIRED_KEY, "true");
  } else {
    localStorage.removeItem(TRIAL_EXPIRED_KEY);
  }
}

export function isTrialExpiredBlocked() {
  const stored = getStoredSubscription();
  if (isSubscriptionCancelled(stored?.schoolId)) {
    setTrialExpiredFlag(false);
    return false;
  }
  const { canAccess, isExpired } = evaluateSubscription(stored);
  if (!canAccess && isExpired) {
    setTrialExpiredFlag(true);
    return true;
  }
  setTrialExpiredFlag(false);
  return localStorage.getItem(TRIAL_EXPIRED_KEY) === "true";
}

function isExpiredTrialMessage(message) {
  const text = String(message ?? "").toLowerCase();
  return (
    text.includes("trial plan has expired") ||
    text.includes("trial has expired") ||
    text.includes("free trial has expired") ||
    text.includes("renew to continue") ||
    text.includes("activate a paid subscription")
  );
}

export function isSubscriptionCancelled(schoolId) {
  if (!schoolId) return false;
  return (
    localStorage.getItem(`${SUBSCRIPTION_CANCELLED_PREFIX}${schoolId}`) === "true"
  );
}

export function markSubscriptionCancelled(schoolId) {
  if (schoolId) {
    localStorage.setItem(`${SUBSCRIPTION_CANCELLED_PREFIX}${schoolId}`, "true");
  }
  setTrialExpiredFlag(false);
}

export async function cancelTrialSubscription(baseURL, signupData = {}) {
  const schoolId = signupData.schoolId ?? getStoredSubscription()?.schoolId;
  if (!baseURL || !schoolId) {
    throw new Error("Unable to cancel subscription. School information is missing.");
  }

  const response = await axios.put(`${baseURL}/school/schoolStatus/${schoolId}`, {
    status: false,
  });

  if (response.data?.code !== 200) {
    throw new Error(
      response.data?.message || "Failed to cancel subscription."
    );
  }

  return response.data;
}

export function completeSubscriptionCancellation(signupData = {}) {
  const schoolId = signupData.schoolId ?? getStoredSubscription()?.schoolId;
  markSubscriptionCancelled(schoolId);
  logoutUser();
}

export function checkAndBlockIfTrialExpired(apiResponse, signupData) {
  if (!apiResponse) return false;

  const schoolId = signupData?.schoolId ?? getStoredSubscription()?.schoolId;
  if (isSubscriptionCancelled(schoolId)) {
    setTrialExpiredFlag(false);
    return false;
  }

  const subscription = extractSubscriptionFromResponse(apiResponse);
  if (subscription) {
    persistSubscription(subscription, signupData);
    const { canAccess, isExpired } = evaluateSubscription(subscription);
    const blocked = !canAccess && isExpired;
    setTrialExpiredFlag(blocked);
    if (blocked) return true;
  }

  if (isExpiredTrialMessage(apiResponse.message)) {
    setTrialExpiredFlag(true);
    return true;
  }

  setTrialExpiredFlag(false);
  return false;
}

export async function fetchSchoolSubscription(baseURL, schoolId) {
  if (!baseURL || !schoolId) return null;

  try {
    const response = await axios.get(
      `${baseURL}/school/subscriptionStatus/${schoolId}`
    );
    if (response.data?.code === 200) {
      return response.data;
    }
  } catch (error) {
    console.error("subscriptionStatus failed:", error);
  }

  return null;
}

export async function verifySchoolSubscriptionBlocked(baseURL, signupData) {
  const schoolId = signupData?.schoolId;
  const schoolCode = signupData?.schoolCode ?? signupData?.verificationCode;
  if (!schoolId) return false;

  if (isSubscriptionCancelled(schoolId)) {
    setTrialExpiredFlag(false);
    return false;
  }

  if (schoolCode) {
    try {
      const verifyResponse = await axios.post(`${baseURL}/school/codeverify`, {
        code: schoolCode,
        schoolId,
      });
      if (verifyResponse.data?.code === 200) {
        return checkAndBlockIfTrialExpired(verifyResponse.data, signupData);
      }
      if (isExpiredTrialMessage(verifyResponse.data?.message)) {
        setTrialExpiredFlag(true);
        return true;
      }
    } catch (error) {
      console.error("codeverify failed:", error);
    }
  }

  const subscriptionResponse = await fetchSchoolSubscription(baseURL, schoolId);
  if (subscriptionResponse) {
    return checkAndBlockIfTrialExpired(subscriptionResponse, signupData);
  }

  return isTrialExpiredBlocked();
}

export function getPaymentRedirectUrl(signupData = {}) {
  const baseUrl = getConfigValue("REACT_APP_PAYMENT_URL");
  if (!baseUrl) return "";

  const schoolId = signupData.schoolId ?? getStoredSubscription()?.schoolId;
  const schoolName = signupData.schoolName ?? getStoredSubscription()?.schoolName;

  try {
    const url = new URL(baseUrl);
    if (schoolId) url.searchParams.set("schoolId", String(schoolId));
    if (schoolName) url.searchParams.set("schoolName", String(schoolName));
    return url.toString();
  } catch {
    const params = new URLSearchParams();
    if (schoolId) params.set("schoolId", String(schoolId));
    if (schoolName) params.set("schoolName", String(schoolName));
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  }
}

export function logoutUser({ keepSignupSession = false } = {}) {
  localStorage.removeItem("access_token");
  localStorage.removeItem("school_type");
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(TRIAL_EXPIRED_KEY);
  if (!keepSignupSession) {
    sessionStorage.removeItem("signupDataSession");
  }
}

export function logoutTrialExpiredUser() {
  logoutUser({ keepSignupSession: true });
}
